import React, { useState, useRef, useEffect } from "react";
import LanguageContext from "../../../helper/LanguageContext";
import { IoClose } from "react-icons/io5";
import "./PollCreationDialog.css";

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 10;

export default function PollCreationDialog({ handleClose, onSend, isInternal }) {
  const { strings } = React.useContext(LanguageContext);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const questionRef = useRef(null);

  useEffect(() => {
    if (questionRef.current) questionRef.current.focus();
  }, []);

  const updateOption = (index, value) => {
    const next = [...options];
    next[index] = value;
    setOptions(next);
  };

  const addOption = () => {
    if (options.length < MAX_OPTIONS) {
      setOptions([...options, ""]);
    }
  };

  const removeOption = (index) => {
    if (options.length > MIN_OPTIONS) {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  const canSend =
    question.trim().length > 0 &&
    options.filter((o) => o.trim().length > 0).length >= MIN_OPTIONS;

  const handleSend = () => {
    if (!canSend) return;
    const trimmedOptions = options
      .map((o) => o.trim())
      .filter((o) => o.length > 0);
    onSend({
      question: question.trim(),
      options: trimmedOptions,
      allowMultiple,
    });
    handleCloseAnimated();
  };

  const handleCloseAnimated = () => {
    setIsClosing(true);
    setTimeout(() => handleClose(), 200);
  };

  return (
    <div
      className={`photo-preview-overlay light ${isClosing ? "closing" : ""}`}
      onClick={handleCloseAnimated}
    >
      <div
        className={`poll-creation-container ${isInternal ? "poll-creation-internal" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="poll-creation-header">
          <h5 className="poll-creation-title">{strings.create_poll}</h5>
          <button className={`kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral${isInternal ? ' kj-btn--internal' : ''}`} onClick={handleCloseAnimated}>
            <IoClose size={20} />
          </button>
        </div>

        <div className="poll-creation-body">
          <label className="poll-creation-label">{strings.poll_question_label}</label>
          <textarea
            ref={questionRef}
            className={`poll-creation-input poll-creation-textarea${question ? " poll-creation-dialog__input--filled" : ""}`}
            placeholder={strings.poll_question_placeholder}
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            maxLength={300}
            rows={1}
          />

          <label className="poll-creation-label mt-3">
            {strings.poll_options_label}
          </label>
          {options.map((opt, i) => (
            <div key={i} className="poll-option-row">
              <input
                type="text"
                className={`poll-creation-input poll-option-input${opt.trim() ? " poll-creation-dialog__input--filled" : ""}`}
                placeholder={`${strings.poll_option} ${i + 1}`}
                value={opt}
                onChange={(e) => updateOption(i, e.target.value)}
                maxLength={100}
                onFocus={() => {
                  if (i === options.length - 1 && options.length < MAX_OPTIONS) {
                    addOption();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (i === options.length - 1 && options.length < MAX_OPTIONS) {
                      addOption();
                    }
                  }
                }}
              />
              {options.length > MIN_OPTIONS && (
                <button
                  className={`kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--round${isInternal ? ' kj-btn--internal' : ''}`}
                  onClick={() => removeOption(i)}
                >
                  <IoClose size={16} />
                </button>
              )}
            </div>
          ))}

          {options.length < MAX_OPTIONS && (
            <button
              className={`poll-add-option-btn kj-btn kj-btn--ghost kj-btn--sm${isInternal ? ' kj-btn--internal' : ''}`}
              onClick={addOption}
            >
              <span className="material-symbols-outlined poll-creation-dialog__add-icon">add</span>
              {strings.poll_add_option}
            </button>
          )}

          <div className="poll-toggle-row">
            <span className="poll-toggle-label">{strings.poll_allow_multiple}</span>
            <label className="poll-toggle-switch">
              <input
                type="checkbox"
                checked={allowMultiple}
                onChange={(e) => setAllowMultiple(e.target.checked)}
              />
              <span
                className={`poll-toggle-slider poll-creation-dialog__slider${allowMultiple ? " poll-creation-dialog__slider--on" : ""}`}
              />
            </label>
          </div>
        </div>

        <div className="poll-creation-footer">
          <button className="kj-btn kj-btn--secondary" onClick={handleCloseAnimated}>
            {strings.cancel}
          </button>
          <button
            className={`kj-btn kj-btn--primary${isInternal ? ' kj-btn--internal' : ''}`}
            disabled={!canSend}
            onClick={handleSend}
          >
            {strings.create || "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
